import { useCallback, useEffect, useMemo, useState } from 'react';
import repairOrdersService from '../services/repairOrders.service';
import { RepairOrderCard, RepairOrderPing, RepairOrderPingType } from '../types/repairOrders';
import { useToast } from '../contexts/ToastContext';

/** Open pings keyed by repair-order recno. */
export type PingsByRepairOrder = Record<number, RepairOrderPing[]>;

/** The repair-order cards the PRO returned, keyed by recno. */
export type CardsByRepairOrder = Record<number, RepairOrderCard>;

export interface UseRepairOrderPingsResult {
  pingsByRo: PingsByRepairOrder;
  /**
   * Only repair orders the PRO actually returned. A ticket whose parsed recno
   * is absent (deleted RO, cross-PRO reference) must render NOTHING rather than
   * guess, so callers key their controls off this map.
   */
  cardsByRo: CardsByRepairOrder;
  /** Ask maintenance for an ETA / a completion status on one repair order. */
  requestPing: (roRecno: number, requestType: RepairOrderPingType) => Promise<void>;
}

/**
 * The repair orders behind a PRO's REPAIR ORDER trouble tickets — their open
 * maintenance pings AND their `orderStatus` — plus the optimistic request
 * action, for the Trouble Ticket panel and modal (a ticket only knows its
 * repair order by the number parsed out of its issue text).
 *
 * Fetched with `getByPro`, not the per-recno `/pings` lookup: ONE call keyed by
 * the PRO covers a whole panel of tickets, and a card carries strictly more
 * than a ping list does — notably the status, without which we cannot tell a
 * finished repair order (no ETA left to ask for) from a live one.
 *
 * ONE call for the whole set, never one per row: the panel routinely lists
 * several REPAIR ORDER tickets for a hot PRO, and a fetch per card is the N+1
 * this hook exists to prevent.
 *
 * The request is optimistic — a synthetic ping goes in immediately so the amber
 * chip appears at once, is swapped for the server's ping on success, and is
 * removed (plus an error toast) on failure. The POST is idempotent server-side,
 * so a duplicate in flight costs nothing.
 *
 * @param pronumber The PRO whose repair orders to load.
 * @param roRecnos  The repair orders the caller's tickets refer to. Used ONLY
 *                  as a "is there anything to look up at all?" gate — an empty
 *                  list skips the fetch entirely, so a panel of hand-written
 *                  tickets costs nothing.
 */
export const useRepairOrderPings = (
  pronumber: number | undefined | null,
  roRecnos: number[],
): UseRepairOrderPingsResult => {
  // A stable primitive key: `roRecnos` is rebuilt every render by the callers
  // (they derive it by parsing ticket issue text), so depending on the array
  // identity would refetch forever.
  const key = useMemo(
    () => Array.from(new Set(roRecnos)).sort((a, b) => a - b).join(','),
    [roRecnos],
  );
  const [pingsByRo, setPingsByRo] = useState<PingsByRepairOrder>({});
  const [cardsByRo, setCardsByRo] = useState<CardsByRepairOrder>({});
  const { showToast } = useToast();

  useEffect(() => {
    if (!key || !pronumber) {
      setPingsByRo({});
      setCardsByRo({});
      return undefined;
    }
    const controller = new AbortController();
    repairOrdersService
      .getByPro(pronumber, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        const nextPings: PingsByRepairOrder = {};
        const nextCards: CardsByRepairOrder = {};
        (res.data || []).forEach((card) => {
          nextCards[card.recno] = card;
          nextPings[card.recno] = card.openPings || [];
        });
        setPingsByRo(nextPings);
        setCardsByRo(nextCards);
      })
      .catch(() => {
        // A failed lookup must not break the surface it decorates: the ticket
        // list / editor is still fully usable without the chips.
      });
    return () => controller.abort();
  }, [key, pronumber]);

  const requestPing = useCallback(
    async (roRecno: number, requestType: RepairOrderPingType) => {
      // Negative, monotonic placeholder id — can never collide with a real recno.
      const tempRecno = -Date.now();
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      // NAIVE local-time ISO, matching the backend's format — see RepairOrderPing.
      const requestedAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

      setPingsByRo((prev) => ({
        ...prev,
        [roRecno]: [
          ...(prev[roRecno] || []),
          { recno: tempRecno, requestType, requestedBy: 'you', requestedAt },
        ],
      }));

      try {
        const res = await repairOrdersService.requestPing(roRecno, requestType);
        setPingsByRo((prev) => ({
          ...prev,
          [roRecno]: (prev[roRecno] || []).map((p) => (p.recno === tempRecno ? res.ping : p)),
        }));
      } catch (err) {
        setPingsByRo((prev) => ({
          ...prev,
          [roRecno]: (prev[roRecno] || []).filter((p) => p.recno !== tempRecno),
        }));
        const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
        const detail = e?.response?.data?.detail;
        showToast({
          type: 'error',
          message: `Ping failed: ${typeof detail === 'string' ? detail : e?.message || 'Request failed'}`,
        });
      }
    },
    [showToast],
  );

  return { pingsByRo, cardsByRo, requestPing };
};

export default useRepairOrderPings;
